{
  class adaModal extends HTMLElement {
    constructor() {
      super()
      this.loaded = false
    }

    connectedCallback() {
      if (this.loaded) {
        return false
      } else {
        this.loaded = true
      }

      // admin preview hack fix because gutenberg doesn't like custom elements
      if(this.hasAttribute('className')){
        this.classList = this.getAttribute('className')
        this.removeAttribute('className')
      }

      if (!this.id) {
        console.warn('ADA modal needs an ID', this)
        this.remove()
        return
      }

      if (!this.querySelectorAll('h1, h2, h3, h4, h5, h6').length) {
        console.warn('#' + this.id + ' should have a heading')
      }

      if (!this.querySelectorAll('button[is="ada-modal-closer"],button[is="ada-modal-toggler"]').length) {
        console.warn('#' + this.id + ' should have a modal closer')
      }

      this.lastOpenedWithButton = null

      // put inside a backdrop and move that to the end of the document
      this.backdrop = document.createElement('ada-modal-backdrop')
      document.body.appendChild(this.backdrop)
      this.backdrop.appendChild(this)

      if(!window.coolHelperFunctions.prefersReducedMotion()){
        this.backdrop.style.transition = "250ms"
        this.style.transition = "250ms"
      }
      this.style.display = "block"
      this.backdrop.style.display = 'none'
      this.backdrop.style.opacity = 0
      // backdrop stays a presentational overlay; aria-hidden tracks open state for the Escape handler
      this.backdrop.setAttribute('aria-hidden', true)

      // dialog semantics live on the modal element itself, not the backdrop
      this.setAttribute('role', 'dialog')
      this.setAttribute('aria-modal', true)

      // auto-derive an accessible name on the modal (no per-instance authoring required)
      const authorLabel = this.getAttribute('title') || this.getAttribute('aria-label')
      if (authorLabel) {
        // prefer title; aria-label is disallowed on the modal per the a11y audit
        this.setAttribute('title', authorLabel)
        this.removeAttribute('aria-label')
      } else {
        const heading = this.querySelector('h1, h2, h3, h4, h5, h6')
        if (heading) {
          if (!heading.id) {
            heading.id = window.coolHelperFunctions.generateID()
          }
          this.setAttribute('aria-labelledby', heading.id)
        } else {
          this.setAttribute('title', 'Dialog')
        }
      }

      // describedby points to the content, now on the modal element
      const contentId = this.getAttribute('data-content-id')
      if (contentId) {
        this.setAttribute('aria-describedby', contentId)
      }

      document.addEventListener('keydown', e => this._handleTab(e))
      document.addEventListener('click', e => {
        const closestA = e.target.closest('a')
        if(closestA && closestA.hash == '#' + this.id){
          this.lastOpenedWithButton = closestA
          this.openModal()
        }
      })

      // sentinels catch focus escaping past a cross-origin iframe: once focus is inside
      // one of those, our keydown listener can no longer see Tab presses to trap them,
      // but the browser still fires a real `focus` event here when it exits the iframe
      this._focusSentinelStart = this._makeFocusSentinel(() => {
        const focusableElements = this._getFocusableElements()
        if (focusableElements.length) focusableElements[focusableElements.length - 1].focus()
      })
      this._focusSentinelEnd = this._makeFocusSentinel(() => {
        const focusableElements = this._getFocusableElements()
        if (focusableElements.length) focusableElements[0].focus()
      })
      this.insertBefore(this._focusSentinelStart, this.firstChild)
      this.appendChild(this._focusSentinelEnd)

      // open automatically
      // if (this.getAttribute('open-on-load') == 'true' || this.getAttribute('open-on-load') == 1) {
      //   this.openModal()
      // }
      const autoOpenDelay = parseInt(this.getAttribute('auto-open-delay'))
      if(this.hasAttribute('auto-open-delay') && autoOpenDelay >= 0){
        setTimeout(() => {
          if(this.hasAttribute('hide-for-days-after-seen')){
            if(this._shouldAutoOpen()){
              this._setHideForDays()
              this.openModal()
            }
          } else {
            this.openModal()
          }
        }, autoOpenDelay)
      }
    }

    _getHashedContent(){
      const text = this.innerText.replace(/[^A-Za-z0-9]/g, '').toLowerCase();
      let hash = 0;
      if (text.length === 0) return hash;
      for (let i = 0; i < text.length; i++) {
        const char = text.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32-bit integer
      }
      return hash;
    }

    _shouldAutoOpen(){
      const name = `popup${this._getHashedContent()}`
      const hideSetting = this.getAttribute('hide-for-days-after-seen')
      if(hideSetting === "undefined"){
        return !window.coolHelperFunctions.getTemporarySessionStorage(name)
      } else {
        return !window.coolHelperFunctions.getTemporaryLocalStorage(name)
      }
    }
    
    _setHideForDays(){
      const name = `popup${this._getHashedContent()}`;
      const hideSetting = this.getAttribute('hide-for-days-after-seen')
      if(hideSetting === "undefined"){
        return !window.coolHelperFunctions.setTemporarySessionStorage(name, 1)
      } else {
        return !window.coolHelperFunctions.setTemporaryLocalStorage(name, 1, parseInt(hideSetting), 'days')
      }
    }

    // invisible, focusable element used to detect focus escaping the modal (see connectedCallback)
    _makeFocusSentinel(onFocus) {
      const sentinel = document.createElement('div')
      sentinel.setAttribute('tabindex', '0')
      sentinel.setAttribute('aria-hidden', 'true')
      sentinel.style.cssText = 'position:fixed;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;'
      sentinel.addEventListener('focus', () => {
        if (this.isOpen()) onFocus()
      })
      return sentinel
    }

    // add all the elements inside modal which you want to make focusable
    _getFocusableElements(excludeCloser = false) {
      const selector = excludeCloser
        ? 'button:not([is="ada-modal-closer"]), [href], input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])'
        : 'button, [href], input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])'
      return Array.from(this.querySelectorAll(selector)).filter(el => (
        el !== this._focusSentinelStart && el !== this._focusSentinelEnd
      ))
    }

    _handleTab(e){
      const isTabPressed = e.key === 'Tab'

      if (!isTabPressed || !this.isOpen()) {
        return
      }

      const focusableElements = this._getFocusableElements()
      const firstFocusableElement = focusableElements.length ? focusableElements[0] : this // get first element to be focused inside modal
      const lastFocusableElement = focusableElements.length ? focusableElements[focusableElements.length - 1] : this // get last element to be focused inside modal

      if (e.shiftKey) { // if shift key pressed for shift + tab combination
        if (document.activeElement === firstFocusableElement) {
          lastFocusableElement.focus() // add focus for the last focusable element
          e.preventDefault()
        }
      } else { // if tab key is pressed
        if (document.activeElement === lastFocusableElement) { // if focused has reached to last focusable element then focus first focusable element after pressing tab
          firstFocusableElement.focus() // add focus for the first focusable element
          e.preventDefault()
        }
      }
    }

    openModal() {
      if(document.activeElement && document.activeElement.getAttribute('aria-controls') == this.id){
        this.lastOpenedWithButton = document.activeElement
      }

      // update aria and display
      this.backdrop.setAttribute('aria-hidden', false)
      if(!window.coolHelperFunctions.prefersReducedMotion()){
        this.backdrop.style.opacity = 1
        this.style.transform = "translateY(100px)" 
        this.style.opacity = 0
        setTimeout(() => {
          this.style.transform = ""
          this.style.opacity = ""
        }, 100)
      }
      this.backdrop.style.display = 'flex'
      this.backdrop.style.zIndex = 2147483647

      // disable scroll
      document.body.style.top = `-${window.scrollY}px`
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'     

      // set timeout so things display before running this stuff
      setTimeout(() => {
        const focusableElements = this._getFocusableElements(true)
        if(focusableElements.length){
          focusableElements[0].focus()
        } else {
          this.focus()
        }
    
        // send an open event
        this.dispatchEvent(new Event('open'))
      })
    }

    closeModal() {
      // enables scrolling again
      const scrollY = document.body.style.top
      document.documentElement.style.scrollBehavior = "auto"
      setTimeout(() => {
        document.body.style.top = ''
        document.body.style.position = ''
        document.body.style.width = ''
        window.scrollTo(0, parseInt(scrollY || '0') * -1)
        setTimeout(() => document.documentElement.style.scrollBehavior = "smooth")
      })

      //update aria and display 
      this.backdrop.setAttribute('aria-hidden', true)
      this.backdrop.style.display = 'none'
      
      this.dispatchEvent(new Event('close'))

      if (this.lastOpenedWithButton) {
        this.lastOpenedWithButton.focus()
      } else {
        const anything = document.querySelector('button:not([is="ada-modal-closer"]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
        if(anything){
          anything.focus()
        }
      }

      this.backdrop.querySelectorAll('iframe').forEach(iframe => {
        let command = {
          "event": "command",
          "func": "pauseVideo"
        }
        if(iframe.src.indexOf('vimeo') != -1) {
          command = {
            "method": "pause",
            "value": "true"
          }
        }
        iframe.contentWindow.postMessage(JSON.stringify(command), "*")
      })
    }

    isOpen(){
      return this.offsetHeight > 0
    }
  }

  class adaModalOpener extends HTMLButtonElement {
    connectedCallback() {
      if (this.loaded) {
        return false
      } else {
        this.loaded = true
      }

      // admin preview hack fix because gutenberg doesn't like custom elements
      if(this.hasAttribute('className')){
        this.classList = this.getAttribute('className')
        this.removeAttribute('className')
      }      

      this.addEventListener('click', (event) => {
        event.preventDefault()
        const modal = document.querySelector('ada-modal#' + this.getAttribute('aria-controls'))
        if(modal && !modal.isOpen()){
          modal.openModal()
        }
      })
    }
  }

  class adaModalCloser extends HTMLButtonElement {
    connectedCallback() {
      if (this.loaded) {
        return false
      } else {
        this.loaded = true
      }

      // admin preview hack fix because gutenberg doesn't like custom elements
      if(this.hasAttribute('className')){
        this.classList = this.getAttribute('className')
        this.removeAttribute('className')
      }

      this.addEventListener('click', (event) => {
        event.preventDefault()
        const modal = document.querySelector('ada-modal#' + this.getAttribute('aria-controls'))
        if(modal && modal.isOpen()){
          modal.closeModal()
        }
      })
    }
  }

  class adaModalBackdrop extends HTMLElement {
    connectedCallback() {
      if (this.loaded) {
        return false
      } else {
        this.loaded = true
      }

      // admin preview hack fix because gutenberg doesn't like custom elements
      if(this.hasAttribute('className')){
        this.classList = this.getAttribute('className')
        this.removeAttribute('className')
      }      

      // listen for escape
      document.addEventListener('keydown', e => {
        const modal = this.querySelector('ada-modal')
        if(modal && modal.getAttribute('esc-to-close') !== 'false' && e.key == 'Escape' && this.getAttribute('aria-hidden') == 'false') {
          modal.closeModal()
        }
      })

      this.addEventListener('click', e => {
        const modal = this.querySelector('ada-modal')
        if(modal && modal.getAttribute('click-backdrop-to-close') != 'false' && e.target == this) {
          modal.closeModal()
        }
      })
    }
  }

  class adaModalToggler extends HTMLButtonElement {
    connectedCallback() {
      if (this.loaded) {
        return false
      } else {
        this.loaded = true
      }

      // admin preview hack fix because gutenberg doesn't like custom elements
      if(this.hasAttribute('className')){
        this.classList = this.getAttribute('className')
        this.removeAttribute('className')
      }

      if(!this.hasAttribute('aria-label')){
        console.warn(this, " may need an aria-label!")
      }

      this.addEventListener('click', (event) => {
        event.preventDefault()
        const modal = document.querySelector('ada-modal#' + this.getAttribute('aria-controls'))
        if(modal && modal.isOpen()){
          modal.closeModal()
        } else {
          modal.openModal()
        }
      })
    }
  }  

  customElements.define('ada-modal', adaModal);
  customElements.define('ada-modal-backdrop', adaModalBackdrop);
  customElements.define('ada-modal-opener', adaModalOpener, { extends: 'button' });
  customElements.define('ada-modal-closer', adaModalCloser, { extends: 'button' });
  customElements.define('ada-modal-toggler', adaModalToggler, { extends: 'button' });
}